import React from 'react';
import { Box, Typography } from '@mui/material';
import DatasetWidgetBuilder from '../../DataImport/DatasetWidgetBuilder';
import { DataImportAnswer } from '../../../support/assessments';

/**
 * Answer input for a `data_import` question — drop an Excel/CSV file and turn it into widgets.
 *
 * The heavy lifting (parse → preset/generic → preview → persist) lives in `DatasetWidgetBuilder`;
 * this only maps the builder's save callback to the assessment answer. The answer records a reference
 * to the persisted `ImportedDataset` and its `CustomTile`s — never the parsed bytes — so the report
 * and dashboards can re-render the widgets independently.
 */

type Props = {
  value: DataImportAnswer | null;
  onChange: (value: DataImportAnswer | null) => void;
  disabled?: boolean;
  /** The response set being filled, so the dataset can be linked to it. Null while still unsaved. */
  responseSetId?: number | null;
  respondent?: string | null;
  /**
   * Salesforce AccountId resolved from the coupled `sf_account` answer. When present, the raw
   * uploaded file is also attached to that account as a Salesforce File. Null → parse only.
   */
  accountId?: string | null;
};

const DataImportInput: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  responseSetId,
  respondent,
  accountId,
}) => (
  <Box>
    <DatasetWidgetBuilder
      responseSetId={responseSetId ?? null}
      updatedBy={respondent ?? null}
      initialSaved={value ?? null}
      disabled={disabled}
      accountId={accountId ?? null}
      onSaved={(ref) => onChange(ref)}
    />
    <Typography variant="caption" color="text.secondary">
      The imported spreadsheet and generated widgets are saved as reusable dashboard tiles; the answer
      records a reference to them.
    </Typography>
  </Box>
);

export default DataImportInput;
